import { ICellRendererComp, ICellRendererParams } from 'ag-grid-community'
import { ValidationIssue } from 'api'
import { isNil } from 'lodash-es'
import tippy, { Instance, Props } from 'tippy.js'
import { getErrorMessages, getIssuesForField, maxSeverity, Severity } from 'model'
import { getTooltipHtml, VALIDATION_TOOLTIP_OPTIONS } from 'view/components/tooltip'
import './ValidationCellRenderer.css'
import { RowDataGetter } from 'view/shared/datagrid'

export interface ValidationCellRendererParams extends ICellRendererParams {
  getValidationIssues?: (params: ValidationCellRendererParams) => ValidationIssue[]
  validationField?: string | ((rowData: any) => string | null)
  rowDataGetter?: RowDataGetter<any, any>
  dataTestId?: string
  informativeTooltip?: string
}

export class ValidationCellRenderer implements ICellRendererComp {
  private eGui: HTMLDivElement | null = null
  private eTooltip: Instance | null = null

  protected getServerFieldValidationIssues(params: ValidationCellRendererParams): ValidationIssue[] {
    if (isNil(params.validationField)) {
      return []
    }

    const rowData = params.rowDataGetter ? params.rowDataGetter(params, true) : params.node.data
    if (isNil(rowData)) {
      return []
    }

    const validationField = typeof params.validationField === 'string' ? params.validationField : params.validationField(rowData)
    if (isNil(validationField)) {
      return []
    }

    return getIssuesForField(rowData?.validationIssues || [], validationField)
  }

  protected getLocalValidationIssues(params: ValidationCellRendererParams): ValidationIssue[] {
    if (isNil(params.getValidationIssues)) {
      return []
    }
    return params.getValidationIssues(params)
  }

  protected getFieldValidationIssues(params: ValidationCellRendererParams) {
    return this.getServerFieldValidationIssues(params).concat(this.getLocalValidationIssues(params))
  }

  private refreshContent(element: HTMLElement, params: ValidationCellRendererParams) {
    element.innerHTML = params.valueFormatted ?? params.value ?? null
  }

  private refreshClasses(element: HTMLElement, params: ValidationCellRendererParams) {
    const validationIssues = this.getFieldValidationIssues(params)
    const severity = maxSeverity(validationIssues)
    element.classList.toggle('severity-error', severity === Severity.ERROR)
    element.classList.toggle('severity-warn', severity === Severity.WARN)
    element.classList.toggle('severity-info', severity === Severity.INFO)
  }

  private refreshTooltip(tooltip: Instance, params: ValidationCellRendererParams) {
    const validationIssues = this.getFieldValidationIssues(params)
    let messages = getErrorMessages(validationIssues)
    if (params.informativeTooltip) {
      messages.push(params.informativeTooltip)
    }
    if (messages.length > 0) {
      tooltip.setContent(getTooltipHtml(messages))
      tooltip.enable()
    } else {
      tooltip.hide()
      tooltip.disable()
    }
  }

  protected refreshCell(element: HTMLElement, tooltip: Instance, params: ValidationCellRendererParams) {
    this.refreshClasses(element, params)
    this.refreshContent(element, params)
    this.refreshTooltip(tooltip, params)
  }

  /* ICellRendererComp */

  public init(params: ValidationCellRendererParams) {
    this.eGui = document.createElement('div')
    if (params.dataTestId) {
      this.getGui().setAttribute('data-test-id', params.dataTestId)
    }
    this.eGui.classList.add('validation-cell')
    this.eTooltip = tippy(this.eGui, VALIDATION_TOOLTIP_OPTIONS as Props) as Instance
    this.eTooltip.disable()
    this.refresh(params)
  }

  public getGui(): HTMLElement {
    if (this.eGui) {
      return this.eGui
    } else {
      throw new Error('eGui is not set')
    }
  }

  public refresh(params: ValidationCellRendererParams): boolean {
    if (this.eGui && this.eTooltip) {
      this.refreshCell(this.eGui, this.eTooltip, params)
      return true
    } else {
      throw new Error('cell has not been initialised')
    }
  }

  public destroy() {
    if (this.eTooltip) {
      this.eTooltip.destroy()
      this.eTooltip = null
    }
    this.eGui = null
  }
}
